import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Environment } from '../constants/environments';
import { logger } from '../utils/logger';

export const FASTPASS_OFFER_SESSION_KEY = 'alchemist_fastpass_offer_id';
export const FASTPASS_OFFER_RESOLVED_PREFIX = 'alchemist_fastpass_resolved_';
const FASTPASS_OFFER_TIMEOUT_MS = 3000;

// Strict UUID v4 (RFC 4122 §4.1.3): version nibble = 4, variant bits = 10xx (8/9/a/b)
const FASTPASS_OFFER_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isValidOfferId = (value: string | null): value is string =>
  typeof value === 'string' && FASTPASS_OFFER_UUID_REGEX.test(value);

interface FastPassOfferResolveResponse {
  offer_id: string;
  final_price: number;
  expires_at: string | null;
}

interface CachedResolvedOffer {
  finalPrice: number;
  expiresAtMs: number;
}

export interface UseFastPassOfferResult {
  offerId: string | null;
  finalPrice: number | null;
  isValid: boolean;
  loading: boolean;
  error: string | null;
}

const isFastPassOfferResolveResponse = (
  payload: unknown
): payload is FastPassOfferResolveResponse => {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return false;
  }

  const candidate = payload as Record<string, unknown>;

  if (
    typeof candidate.offer_id !== 'string' ||
    typeof candidate.final_price !== 'number' ||
    candidate.final_price <= 0 ||
    (candidate.expires_at !== null && typeof candidate.expires_at !== 'string')
  ) {
    return false;
  }

  // null = offer has no expiration (valid indefinitely).
  // Non-null: reject offers already expired by the time the response lands.
  // Backend remains the source of truth at /shop-resale; failing fast here
  // prevents the strikethrough UI from advertising a price the user cannot
  // actually buy. Date.parse returns NaN for invalid ISO — comparison fails closed.
  if (candidate.expires_at !== null) {
    const expiresAtMs = Date.parse(candidate.expires_at as string);
    if (Number.isNaN(expiresAtMs) || expiresAtMs <= Date.now()) {
      return false;
    }
  }

  return true;
};

const readOfferIdFromSessionStorage = (): string | null => {
  try {
    return window.sessionStorage.getItem(FASTPASS_OFFER_SESSION_KEY);
  } catch (storageError) {
    logger.warn('useFastPassOffer: failed to read offerId from sessionStorage', {
      error : storageError instanceof Error ? storageError.message : String(storageError),
    });
    return null;
  }
};

const persistOfferIdToSessionStorage = (offerId: string): void => {
  try {
    window.sessionStorage.setItem(FASTPASS_OFFER_SESSION_KEY, offerId);
  } catch (storageError) {
    logger.warn('useFastPassOffer: failed to persist offerId to sessionStorage', {
      error : storageError instanceof Error ? storageError.message : String(storageError),
    });
  }
};

/**
 * Reads a previously resolved offer from sessionStorage.
 *
 * Idempotency rationale: the Edge Function `GET /fastpass-offer?id=<uuid>` has a
 * server-side side effect — every successful response marks `status=clicked` on
 * the `fastpass_offers` row, which feeds the `automation:link_clicked` PostHog
 * metric. React StrictMode (dev) and F5 mid-journey would otherwise trigger
 * additional resolutions and inflate that metric. Caching the validated
 * `{ finalPrice, expiresAtMs }` per offerId lets the hook short-circuit the
 * fetch on subsequent mounts within the same session.
 *
 * Returns null if the entry is absent, malformed, or already expired (in which
 * case the entry is purged so the next mount can refetch).
 */
const readCachedResolvedOffer = (offerId: string): CachedResolvedOffer | null => {
  const key = `${FASTPASS_OFFER_RESOLVED_PREFIX}${offerId}`;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed) ||
      typeof (parsed as Record<string, unknown>).finalPrice !== 'number' ||
      typeof (parsed as Record<string, unknown>).expiresAtMs !== 'number'
    ) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    const candidate = parsed as CachedResolvedOffer;
    if (candidate.finalPrice <= 0 || candidate.expiresAtMs <= Date.now()) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    return candidate;
  } catch (storageError) {
    logger.warn('useFastPassOffer: failed to read cached offer', {
      offerId,
      error : storageError instanceof Error ? storageError.message : String(storageError),
    });
    return null;
  }
};

const writeCachedResolvedOffer = (offerId: string, entry: CachedResolvedOffer): void => {
  try {
    window.sessionStorage.setItem(
      `${FASTPASS_OFFER_RESOLVED_PREFIX}${offerId}`,
      JSON.stringify(entry)
    );
  } catch (storageError) {
    logger.warn('useFastPassOffer: failed to cache resolved offer', {
      offerId,
      error : storageError instanceof Error ? storageError.message : String(storageError),
    });
  }
};

const clearCachedResolvedOffer = (offerId: string): void => {
  try {
    window.sessionStorage.removeItem(`${FASTPASS_OFFER_RESOLVED_PREFIX}${offerId}`);
  } catch {
    // sessionStorage.removeItem can throw SecurityError in private mode or
    // sandboxed iframes — intentionally ignored (fail-open).
  }
};

/**
 * useFastPassOffer
 *
 * Detects a FastPass offer for a resale purchase flow and resolves its pricing
 * via an Edge Function. Designed to be "fail-open": any network failure,
 * timeout, or invalid payload must NOT break the resale checkout — the hook
 * always returns a consistent shape and lets the caller fall back to the
 * default resale price.
 *
 * Behaviour:
 *  1. Reads `offerId` with priority:
 *     a. URL query param `?offerId=<uuid>`.
 *     b. Fallback to `sessionStorage.getItem('alchemist_fastpass_offer_id')`.
 *  2. If the offerId comes from the URL, persists it in sessionStorage so it
 *     survives navigation from the resale page to the resale-payment page.
 *     If the offerId already came from sessionStorage, it is NOT rewritten.
 *  3. Idempotency: if the offer was already resolved in this session and the
 *     cached entry is still within its TTL, the hook hydrates state from
 *     sessionStorage and SKIPS the network call. The Edge Function `GET` has
 *     a `status=clicked` side effect, so this prevents StrictMode (dev) and
 *     F5 mid-journey from inflating the `automation:link_clicked` metric.
 *  4. On a cache miss, calls
 *     `GET ${Environment.API_FASTPASS_OFFER_URL}/fastpass-offers/<offerId>`
 *     with a 3s AbortController timeout. The offer ID is a path segment
 *     (`/fastpass-offers/{id}`), not a query param. `resaleId` is only a
 *     guard for firing the request — the Edge Function resolves solely by id.
 *  5. On success, returns `{ finalPrice, isValid: true, loading: false }` and
 *     writes the cache entry. The TTL is captured on the monotonic
 *     performance.now() timeline, so the client-side expiration timer is
 *     immune to wall-clock drift during the session.
 *  6. On error/timeout/invalid payload, returns
 *     `{ finalPrice: null, isValid: false, loading: false, error: <message> }`
 *     so the resale flow proceeds with the default price.
 *  7. When the TTL elapses during the session, invalidates the offer
 *     client-side and clears the cache entry (backend remains source of truth
 *     at `/shop-resale`).
 *  8. Aborts any in-flight request on unmount.
 *
 * @param resaleId The resale UUID associated with the potential FastPass offer.
 * @returns Hook state: `{ offerId, finalPrice, isValid, loading, error }`.
 */
export const useFastPassOffer = (
  resaleId: string | undefined
): UseFastPassOfferResult => {
  const [searchParams] = useSearchParams();
  const offerIdFromUrl = searchParams.get('offerId');

  const [offerId, setOfferId] = useState<string | null>(null);
  const [finalPrice, setFinalPrice] = useState<number | null>(null);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic deadline (performance.now() basis) — immune to wall-clock drift
  // during the session. Computed once when the offer resolves; the timer
  // effect just subtracts performance.now() from it.
  const [expiresAtPerf, setExpiresAtPerf] = useState<number | null>(null);

  const posthog = usePostHog();

  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => () => {
    isMountedRef.current = false;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  useEffect(() => {
    const rawUrlOfferId = offerIdFromUrl;
    const rawStoredOfferId = readOfferIdFromSessionStorage();
    const urlOfferId = isValidOfferId(rawUrlOfferId) ? rawUrlOfferId : null;
    const storedOfferId = isValidOfferId(rawStoredOfferId) ? rawStoredOfferId : null;
    // Auto-cleanup: purge contaminated sessionStorage value.
    // sessionStorage.removeItem can throw SecurityError in private mode or
    // sandboxed iframes — intentionally ignored (fail-open).
    if (rawStoredOfferId && !storedOfferId) {
      try { window.sessionStorage.removeItem(FASTPASS_OFFER_SESSION_KEY); } catch { /* see comment above */ }
    }
    const resolvedOfferId = urlOfferId ?? storedOfferId;

    setOfferId(resolvedOfferId);

    if (!resolvedOfferId || !resaleId) {
      setFinalPrice(null);
      setIsValid(false);
      setLoading(false);
      setError(null);
      setExpiresAtPerf(null);
      return;
    }

    // Idempotency short-circuit: if this offer was already resolved in this
    // session and is still within its TTL, hydrate the state from cache and
    // skip the network call. This prevents StrictMode (dev), F5 mid-journey,
    // and concurrent provider mounts from re-marking `status=clicked` on the
    // Edge Function (each call would inflate `automation:link_clicked`).
    const cached = readCachedResolvedOffer(resolvedOfferId);
    if (cached) {
      // Persist offerId from URL into sessionStorage even on cache hit so the
      // resale -> resale-payment hop survives without depending on the URL
      // being preserved across navigations.
      if (urlOfferId && urlOfferId !== storedOfferId) {
        persistOfferIdToSessionStorage(urlOfferId);
      }
      const remainingMs = cached.expiresAtMs - Date.now();
      // MAX_SAFE_INTEGER marks a no-expiry offer — don't set a deadline.
      const cachedDeadline = remainingMs > 2_147_483_647 ? null : performance.now() + remainingMs;
      setFinalPrice(cached.finalPrice);
      setIsValid(true);
      setError(null);
      setLoading(false);
      setExpiresAtPerf(cachedDeadline);
      logger.debug('useFastPassOffer: hydrated from cache', {
        offerId : resolvedOfferId,
        finalPrice : cached.finalPrice,
      });
      return;
    }

    const baseUrl = Environment.API_FASTPASS_OFFER_URL;
    if (!baseUrl) {
      logger.warn('useFastPassOffer: API_FASTPASS_OFFER_URL is not configured');
      setFinalPrice(null);
      setIsValid(false);
      setLoading(false);
      setError('missing_configuration');
      setExpiresAtPerf(null);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const timeoutId = window.setTimeout(() => {
      controller.abort();
    }, FASTPASS_OFFER_TIMEOUT_MS);

    setLoading(true);
    setError(null);

    // Edge Function path: GET /fastpass-offers/{offer_id} (path segment, not query param).
    // NOTE: this GET is not idempotent — the Edge Function marks
    // status=clicked on fastpass_offers each successful call (drives the
    // automation:link_clicked PostHog metric). The hook is lifted into
    // FastPassOfferProvider precisely so the resale journey emits a single
    // click per offerId; do not call this URL outside the provider.
    const url = `${baseUrl.replace(/\/$/, '')}/fastpass-offers/${encodeURIComponent(resolvedOfferId)}`;

    const resolveOffer = async (): Promise<void> => {
      try {
        const response = await fetch(url, {
          method : 'GET',
          signal : controller.signal,
          headers : {
            Accept : 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`unexpected_status_${response.status}`);
        }

        const payload: unknown = await response.json();

        if (!isFastPassOfferResolveResponse(payload)) {
          throw new Error('invalid_response_shape');
        }

        if (!isMountedRef.current || controller.signal.aborted) {
          return;
        }

        // Persist only after successful validation; only if offer came from URL
        if (urlOfferId && urlOfferId !== storedOfferId) {
          persistOfferIdToSessionStorage(urlOfferId);
        }

        // Wire format is centavos (bigint); convert to reais at the boundary
        // so all downstream consumers (display, comparisons, GA4) use the
        // same unit as ResaleData.saleValue (reais).
        const finalPriceReais = payload.final_price / 100;

        // null expires_at = offer has no expiration — no client timer is
        // registered and the cache entry lives for the session. Non-null:
        // capture TTL once using the monotonic clock so the deadline is
        // immune to wall-clock drift during the session.
        let deadlinePerf: number | null = null;
        let expiresAtWallMs: number = Number.MAX_SAFE_INTEGER;
        if (payload.expires_at !== null) {
          expiresAtWallMs = Date.parse(payload.expires_at as string);
          deadlinePerf = performance.now() + (expiresAtWallMs - Date.now());
        }

        writeCachedResolvedOffer(resolvedOfferId, {
          finalPrice : finalPriceReais,
          expiresAtMs : expiresAtWallMs,
        });

        setFinalPrice(finalPriceReais);
        setIsValid(true);
        setError(null);
        setExpiresAtPerf(deadlinePerf);
        logger.debug('useFastPassOffer: offer resolved', {
          offerId : resolvedOfferId,
          finalPrice : finalPriceReais,
        });
      } catch (resolveError) {
        if (!isMountedRef.current) {
          return;
        }

        const message =
          resolveError instanceof Error ? resolveError.message : String(resolveError);
        const isAbort =
          controller.signal.aborted ||
          (resolveError instanceof DOMException && resolveError.name === 'AbortError');
        const normalizedMessage = isAbort ? 'timeout' : message;

        logger.warn('useFastPassOffer: failed to resolve offer', {
          offerId : resolvedOfferId,
          resaleId,
          error : normalizedMessage,
        });

        setFinalPrice(null);
        setIsValid(false);
        setError(normalizedMessage);
        setExpiresAtPerf(null);
      } finally {
        window.clearTimeout(timeoutId);
        if (isMountedRef.current) {
          setLoading(false);
        }
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    };

    void resolveOffer();

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    };
  }, [offerIdFromUrl, resaleId]);

  // Invalidate the offer client-side as soon as the TTL elapses. Backend
  // remains the source of truth on /shop-resale, but without this timer the
  // strikethrough UI keeps advertising the discount after expiry, misleading
  // the buyer until they try to pay.
  //
  // The deadline is stored on the monotonic performance.now() timeline (set in
  // the fetch handler / cache hydration). All decisions in this effect rely on
  // performance.now(), so the offer cannot be invalidated early or kept alive
  // by a wall-clock change (user/OS clock adjustments, NTP sync).
  useEffect(() => {
    if (!isValid || expiresAtPerf === null) {
      return;
    }

    const remainingMs = expiresAtPerf - performance.now();
    if (remainingMs <= 0) {
      setIsValid(false);
      setFinalPrice(null);
      setError('expired');
      if (offerId) clearCachedResolvedOffer(offerId);
      return;
    }

    // setTimeout delays > 2^31-1 ms (~24.8 days) are clamped to 1 ms in JS,
    // which would fire the invalidation immediately. Real FastPass TTLs are
    // minutes-to-hours; if a payload claims a far-future expiry, skip the
    // client timer and rely on backend rejection at /shop-resale.
    const MAX_SAFE_TIMEOUT_MS = 2_147_483_647;
    if (remainingMs > MAX_SAFE_TIMEOUT_MS) {
      return;
    }

    const timerId = window.setTimeout(() => {
      if (!isMountedRef.current) return;
      setIsValid(false);
      setFinalPrice(null);
      setError('expired');
      if (offerId) clearCachedResolvedOffer(offerId);
      logger.warn('useFastPassOffer: offer expired client-side', { offerId });
      posthog?.capture('fastpass_offer_abandoned', {
        offer_id : offerId,
        reason : 'client_side_expiry',
      });
    }, remainingMs);

    return () => window.clearTimeout(timerId);
  }, [isValid, expiresAtPerf, offerId, posthog]);

  return {
    offerId,
    finalPrice,
    isValid,
    loading,
    error,
  };
};

export default useFastPassOffer;
